import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  TrendingUp, TrendingDown, Target, BarChart3, 
  Users, Calendar, DollarSign, Palette
} from 'lucide-react';
import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';

const CreativeHub = ({ isDarkMode = true }) => {
  // Mock data for charts
  const performanceData = [
    { name: 'Mon', campaigns: 12, engagement: 85, reach: 1200 },
    { name: 'Tue', campaigns: 15, engagement: 92, reach: 1400 },
    { name: 'Wed', campaigns: 18, engagement: 78, reach: 1100 },
    { name: 'Thu', campaigns: 22, engagement: 95, reach: 1800 },
    { name: 'Fri', campaigns: 25, engagement: 88, reach: 1600 },
    { name: 'Sat', campaigns: 20, engagement: 82, reach: 1300 },
    { name: 'Sun', campaigns: 16, engagement: 90, reach: 1500 },
  ];

  const recentCampaigns = [
    {
      id: 1,
      title: 'Summer Collection Launch',
      status: 'active',
      performance: 94,
      engagement: 1250,
      reach: 45000,
      platform: 'Instagram',
      image: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400&h=300&fit=crop',
      trend: 'up'
    },
    {
      id: 2,
      title: 'Brand Awareness Campaign',
      status: 'paused',
      performance: 87,
      engagement: 890,
      reach: 32000,
      platform: 'Facebook',
      image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=400&h=300&fit=crop',
      trend: 'down'
    },
    {
      id: 3,
      title: 'Product Showcase',
      status: 'active',
      performance: 96,
      engagement: 2100,
      reach: 78000,
      platform: 'TikTok',
      image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=400&h=300&fit=crop',
      trend: 'up'
    }
  ];

  const quickStats = [
    {
      title: 'Total Campaigns',
      value: '24',
      change: '+12%',
      trend: 'up',
      icon: Target,
      color: 'from-blue-500 to-cyan-500',
      bgColor: 'bg-blue-500'
    },
    {
      title: 'Active Projects',
      value: '18',
      change: '+8%',
      trend: 'up',
      icon: BarChart3,
      color: 'from-green-500 to-emerald-500',
      bgColor: 'bg-green-500'
    },
    {
      title: 'Team Members',
      value: '12',
      change: '+2',
      trend: 'up',
      icon: Users,
      color: 'from-purple-500 to-pink-500',
      bgColor: 'bg-purple-500'
    },
    {
      title: 'Success Rate',
      value: '89%',
      change: '+5%',
      trend: 'up',
      icon: Palette,
      color: 'from-yellow-500 to-orange-500',
      bgColor: 'bg-yellow-500'
    }
  ];

  const teamPerformance = [
    { name: 'Sarah Chen', role: 'Creative Director', projects: 8, performance: 96, avatar: 'SC' },
    { name: 'Mike Johnson', role: 'Graphic Designer', projects: 12, performance: 88, avatar: 'MJ' },
    { name: 'Emma Davis', role: 'Content Creator', projects: 15, performance: 92, avatar: 'ED' },
    { name: 'Alex Kim', role: 'Video Editor', projects: 6, performance: 85, avatar: 'AK' }
  ];

  const recentActivity = [
    { id: 1, action: 'Campaign launched', user: 'Sarah Chen', time: '2 hours ago', type: 'launch' },
    { id: 2, action: 'Design approved', user: 'Mike Johnson', time: '4 hours ago', type: 'approval' },
    { id: 3, action: 'Content uploaded', user: 'Emma Davis', time: '6 hours ago', type: 'upload' },
    { id: 4, action: 'Review completed', user: 'Alex Kim', time: '8 hours ago', type: 'review' }
  ];

  const StatCard = ({ stat }) => (
    <motion.div
      whileHover={{ scale: 1.02 }}
      className="bg-black/20 backdrop-blur-xl rounded-2xl p-6 shadow-2xl border border-white/10 hover:shadow-3xl transition-all duration-300"
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-300">{stat.title}</p>
          <p className="text-2xl font-bold text-white mt-1">{stat.value}</p>
          <div className={`flex items-center mt-2 text-sm ${
            stat.trend === 'up' ? 'text-green-400' : 'text-red-400'
          }`}>
            {stat.trend === 'up' ? (
              <TrendingUp className="h-4 w-4 mr-1" />
            ) : (
              <TrendingDown className="h-4 w-4 mr-1" />
            )}
            <span>{stat.change}</span>
          </div>
        </div>
        <div className={`p-3 rounded-xl bg-gradient-to-r ${stat.color}`}>
          <stat.icon className="h-6 w-6 text-white" />
        </div>
      </div>
    </motion.div>
  );

  const CampaignCard = ({ campaign }) => (
    <motion.div
      whileHover={{ scale: 1.02 }}
      className="bg-black/20 backdrop-blur-xl rounded-2xl overflow-hidden shadow-2xl border border-white/10 hover:shadow-3xl transition-all duration-300"
    >
      <div className="relative h-48 bg-gradient-to-br from-gray-800 to-gray-900">
        <img 
          src={campaign.image} 
          alt={campaign.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
        <div className="absolute top-3 right-3">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
            campaign.status === 'active' 
              ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
              : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
          }`}>
            {campaign.status}
          </span>
        </div>
        <div className="absolute bottom-3 left-3">
          <span className="px-2 py-1 bg-black/50 backdrop-blur-sm text-white text-xs rounded-lg border border-white/20">
            {campaign.platform}
          </span>
        </div>
      </div>
      
      <div className="p-6">
        <h3 className="font-semibold text-white mb-2">{campaign.title}</h3>
        
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-300">Performance</span>
            <div className="flex items-center space-x-2">
              <span className="font-semibold text-white">{campaign.performance}%</span>
              {campaign.trend === 'up' ? (
                <TrendingUp className="h-4 w-4 text-green-400" />
              ) : (
                <TrendingDown className="h-4 w-4 text-red-400" />
              )}
            </div>
          </div>
          
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-300">Engagement</span>
            <span className="font-medium text-white">{campaign.engagement}</span>
          </div>
          
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-300">Reach</span>
            <span className="font-medium text-white">{campaign.reach ? campaign.reach.toLocaleString() : '0'}</span>
          </div>
        </div>
        
        <div className="mt-4 pt-4 border-t border-white/10">
          <div className="flex items-center justify-between">
            <button className="text-purple-400 hover:text-purple-300 text-sm font-medium transition-colors">
              View Details
            </button>
            <div className="flex space-x-2">
              <button className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-all duration-300">
                {/* <Eye className="h-4 w-4" /> */}
              </button>
              <button className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-all duration-300">
                {/* <Share2 className="h-4 w-4" /> */}
              </button>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className="w-full h-full px-8 py-8 space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0"
      >
        <div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-white to-purple-200 bg-clip-text text-transparent">
            Creative Hub
          </h1>
          <p className="text-gray-300 mt-2 text-lg">
            Your central command center for all creative operations
          </p>
        </div>
        <div className="flex space-x-3">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-medium hover:shadow-lg transition-all duration-300 flex items-center space-x-2"
          >
            {/* <Plus className="h-5 w-5" /> */}
            <span>New Campaign</span>
          </motion.button>
        </div>
      </motion.div>

      {/* Quick Stats */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
      >
        {quickStats.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 + index * 0.1 }}
          >
            <StatCard stat={stat} />
          </motion.div>
        ))}
      </motion.div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column - Charts */}
        <div className="lg:col-span-2 space-y-8">
          {/* Performance Chart */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-black/20 backdrop-blur-xl rounded-2xl p-6 shadow-2xl border border-white/10"
          >
            <h2 className="text-xl font-semibold text-white mb-6">Performance Overview</h2>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={performanceData}>
                <defs>
                  <linearGradient id="colorCampaigns" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorEngagement" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="name" stroke="#9CA3AF" />
                <YAxis stroke="#9CA3AF" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'rgba(0, 0, 0, 0.9)', 
                    border: '1px solid rgba(255, 255, 255, 0.1)', 
                    borderRadius: '12px',
                    color: '#fff'
                  }}
                />
                <Area 
                  type="monotone" 
                  dataKey="campaigns" 
                  stroke="#8B5CF6" 
                  strokeWidth={3}
                  fillOpacity={1} 
                  fill="url(#colorCampaigns)" 
                />
                <Area 
                  type="monotone" 
                  dataKey="engagement" 
                  stroke="#10B981" 
                  strokeWidth={3}
                  fillOpacity={1} 
                  fill="url(#colorEngagement)" 
                />
              </AreaChart>
            </ResponsiveContainer>
          </motion.div>

          {/* Recent Campaigns */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-black/20 backdrop-blur-xl rounded-2xl p-6 shadow-2xl border border-white/10"
          >
            <h2 className="text-xl font-semibold text-white mb-6">Recent Campaigns</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {recentCampaigns.map((campaign, index) => (
                <motion.div
                  key={campaign.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + index * 0.1 }}
                >
                  <CampaignCard campaign={campaign} />
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Right Column - Team & Activity */}
        <div className="space-y-8">
          {/* Team Performance */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-black/20 backdrop-blur-xl rounded-2xl p-6 shadow-2xl border border-white/10"
          >
            <h2 className="text-xl font-semibold text-white mb-4">Top Performers</h2>
            <div className="space-y-4">
              {teamPerformance.map((member, index) => (
                <motion.div
                  key={member.name}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + index * 0.1 }}
                  className="flex items-center space-x-4 p-4 bg-white/5 rounded-xl hover:bg-white/10 transition-all duration-300"
                >
                  <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                    {member.avatar}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-white">{member.name}</p>
                    <p className="text-sm text-gray-400">{member.role}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-white">{member.performance}%</p>
                    <p className="text-xs text-gray-400">{member.projects} projects</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Recent Activity */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-black/20 backdrop-blur-xl rounded-2xl p-6 shadow-2xl border border-white/10"
          >
            <h2 className="text-xl font-semibold text-white mb-4">Recent Activity</h2>
            <div className="space-y-4">
              {recentActivity.map((activity, index) => (
                <motion.div
                  key={activity.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + index * 0.1 }}
                  className="flex items-center space-x-3 p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-all duration-300"
                >
                  <div className={`w-2 h-2 rounded-full ${
                    activity.type === 'launch' ? 'bg-green-500' :
                    activity.type === 'approval' ? 'bg-blue-500' :
                    activity.type === 'upload' ? 'bg-purple-500' :
                    'bg-gray-500'
                  }`} />
                  <div className="flex-1">
                    <p className="text-sm text-white">{activity.action}</p>
                    <p className="text-xs text-gray-400">by {activity.user}</p>
                  </div>
                  <span className="text-xs text-gray-400">{activity.time}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default CreativeHub; 